/**
 * Panda CSS styles for HALO Card component
 *
 * Uses Class Variance Authority (cva) for systematic variant generation
 * Implements 8px border radius, 1px border, and flexible padding variants
 */

import { css, cva } from '@/styled-system/css';

/**
 * Base card styles shared across all variants
 * Implements core visual specifications from requirements
 */
export const cardBase = css({
  borderRadius: 'md', // Per specification
  borderWidth: 'default',
  borderStyle: 'solid',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  outline: 'none',
});

/**
 * Card variant styles using Class Variance Authority (cva)
 * Systematic approach to variant combinations
 */
export const cardVariants = cva({
  variants: {
    variant: {
      default: {
        backgroundColor: 'bg.neutral', // Per specification
        borderColor: 'border.neutral', // Per specification
      },
      accent: {
        // HP-7690: Constellation has no semantic token matching this brand-yellow
        // surface, so the raw token preserves the legacy brand-yellow value.
        backgroundColor: 'yellow.400',
        borderColor: 'transparent', // No border for accent variant
      },
      minimal: {
        backgroundColor: 'transparent',
        borderColor: 'transparent',
      },
    },

    size: {
      sm: {
        padding: 'tighter', // 8px - compact layouts
      },
      md: {
        padding: 'default', // 16px - default per specification
      },
      lg: {
        padding: 'loose', // 24px - spacious layouts
      },
    },

    collapsible: {
      true: {
        // Additional styles for interactive cards
        cursor: 'default',
      },
      false: {},
    },
  },

  defaultVariants: {
    variant: 'default',
    size: 'md',
    collapsible: false,
  },
});

/**
 * Card header styles with flexible layout variants
 * Replaces fixed cardHeader with CVA system for layout flexibility
 */
export const cardHeaderVariants = cva({
  base: {
    display: 'flex',
    gap: 'tighter', // 8px spacing between elements
    marginBottom: 'default', // 16px separation from body
    minHeight: '600', // Ensure consistent height even without content
  },
  variants: {
    layout: {
      default: { justifyContent: 'flex-start' },
      'space-between': { justifyContent: 'space-between' },
      'space-around': { justifyContent: 'space-around' },
      'flex-start': { justifyContent: 'flex-start' },
      'flex-end': { justifyContent: 'flex-end' },
    },
    align: {
      'flex-start': { alignItems: 'flex-start' },
      center: { alignItems: 'center' },
      'flex-end': { alignItems: 'flex-end' },
      baseline: { alignItems: 'baseline' },
    },
  },
  defaultVariants: {
    layout: 'default',
    align: 'center', // Maintains backward compatibility
  },
});

/**
 * Legacy cardHeader for backward compatibility
 * @deprecated Use cardHeaderVariants with default variants instead
 */
export const cardHeader = css({
  display: 'flex',
  alignItems: 'center',
  gap: 'tighter',
  marginBottom: 'default',
  minHeight: '600',
});

/**
 * Card body styles
 * Flexible content area with collapsible behavior
 */
export const cardBody = css({
  display: 'flex',
  flexDirection: 'column',
  flex: 1, // Take remaining space

  '&[data-collapsed="true"]': {
    display: 'none', // Hidden when collapsed for progressive enhancement
  },
});

/**
 * Card title styles with size variants
 * Implements typography specifications
 */
export const cardTitle = css({
  fontSize: 'heading-xs', // 20px - default per specification
  fontWeight: 'bold', // Per specification
  color: 'text.neutral', // Per specification
  margin: '0',
  lineHeight: 'heading-xs',

  '&[data-size="md"]': {
    fontSize: 'body', // 16px variant for smaller titles
  },
});

/**
 * Card icon wrapper styles
 * Proper spacing and alignment for icons
 */
export const cardIcon = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',

  flexShrink: 0,
  fontSize: 'body', // 16px - balanced with title
  lineHeight: 'body',
  color: 'text.neutral', // Match title color
});

/**
 * Collapse toggle button styles
 * Interactive caret button for collapsible functionality
 */
export const cardCollapseToggle = css({
  marginLeft: 'auto', // Push to right side of header
  cursor: 'pointer',
  padding: 'tightest', // 4px - minimal touch target padding
  borderRadius: 'sm',
  border: 'none',
  backgroundColor: 'transparent',
  color: 'text.neutral',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 'body',
  transitionProperty: 'background-color',
  transitionDuration: '0.2s',
  transitionTimingFunction: 'ease-in-out',
  minWidth: '600',
  minHeight: '600',

  _hover: {
    backgroundColor: 'bg.softest',
  },

  _focusVisible: {
    outlineWidth: 'thick',
    outlineStyle: 'solid',
    outlineColor: 'brand.primary',
    outlineOffset: '50',
  },

  _active: {
    backgroundColor: 'bg.softer',
  },
});

/**
 * Card footer styles
 * Optional footer area for actions or additional content
 */
export const cardFooter = css({
  marginTop: 'default', // 16px separation from body
  display: 'flex',
  flexDirection: 'column',
});
